/* eslint-disable react/no-array-index-key */
import {formattedPropertyModel, intlShape, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {cloneDeep, omit} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import styles from './ChangeItemContentItem.less';
import {constants} from 'src/js/common';
import {KEYWORD_PROP_CONTENT_VALUE} from 'src/js/model/auto_layout_screen_data_helpers';

const CHANGE_FIELD_PREFIX = 'autoLayout.object.changeItem.field.';
const LABEL_CODE = 'labelCode';
const LABEL = 'label';
const ID = 'id';
const NAME = 'name';
const TYPE = 'type';
const TYPE_ARRAY = 'array';
export const CHANGE_TYPE = 'changeType';
export const CHANGE_TYPE_CHANGED = 'changed';
export const CHANGE_TYPE_ADDED = 'added';
export const CHANGE_TYPE_REMOVED = 'removed';
const EMPTY_VALUE = '-----';

const logger = utils.LogUtils.getLogger(
  'ChangeItemContentItem',
  constants.LIBRARY_NAME
);

const I18N = defineMessages({
  CHANGED: {
    id: 'autoLayout.object.changeItem.changed',
    defaultMessage: 'changed ',
  },
  ADDED: {
    id: 'autoLayout.object.changeItem.added',
    defaultMessage: 'added ',
  },
  REMOVED: {
    id: 'autoLayout.object.changeItem.removed',
    defaultMessage: 'removed ',
  },
  TO: {
    id: 'autoLayout.object.changeItem.to',
    defaultMessage: 'to ',
  },
});

export function splitByChangeTypes(field) {
  const splitResult = [];
  if (Object.prototype.hasOwnProperty.call(field, CHANGE_TYPE_ADDED)) {
    // change type added is detected, add to result
    splitResult.push(omit(cloneDeep(field), [CHANGE_TYPE_REMOVED]));
    if (Object.prototype.hasOwnProperty.call(field, CHANGE_TYPE_REMOVED)) {
      // also change type removed is detected, add to result
      splitResult.push(omit(cloneDeep(field), [CHANGE_TYPE_ADDED]));
    }
  } else {
    // only change type removed or none (changed) is detected, add to result
    splitResult.push(field);
  }
  return splitResult;
}

export function getChangeType(field) {
  let changeType = CHANGE_TYPE_CHANGED;
  if (Object.prototype.hasOwnProperty.call(field, CHANGE_TYPE_ADDED)) {
    changeType = CHANGE_TYPE_ADDED;
  } else if (Object.prototype.hasOwnProperty.call(field, CHANGE_TYPE_REMOVED)) {
    changeType = CHANGE_TYPE_REMOVED;
  }
  return changeType;
}

function getValueFromFieldContent(fieldContent, intl, fieldType) {
  let fieldValue;

  if (fieldContent == null) {
    // value is not set - null or undefined
    fieldValue = EMPTY_VALUE;
  } else if (fieldType) {
    // value has type, apply type formatter
    const propDefinition = new formattedPropertyModel.FormattedProperty({
      type: fieldType,
    });
    fieldValue = formattedPropertyModel.FormattedProperty.formatValue(
      propDefinition,
      fieldContent,
      intl
    );
  } else if (
    // value has translation in LABEL_CODE, translate it
    fieldContent[LABEL_CODE] &&
    utils.TranslateUtils.hasTranslation(fieldContent[LABEL_CODE], intl)
  ) {
    fieldValue = utils.TranslateUtils.translate(fieldContent[LABEL_CODE], intl);
  } else if (
    // value has label
    fieldContent[LABEL]
  ) {
    // if it has translation, translate it, otherwise use raw label
    fieldValue = utils.TranslateUtils.hasTranslation(fieldContent[LABEL], intl)
      ? utils.TranslateUtils.translate(fieldContent[LABEL], intl)
      : fieldContent[LABEL];
  } else {
    // value is raw value
    fieldValue = fieldContent[ID] || fieldContent || EMPTY_VALUE;
  }
  return fieldValue;
}

function getFieldValue(fieldData, intl) {
  if (fieldData && fieldData[NAME]) {
    let fieldContent = fieldData[KEYWORD_PROP_CONTENT_VALUE];
    if (fieldData[CHANGE_TYPE] === CHANGE_TYPE_ADDED) {
      fieldContent = fieldData[CHANGE_TYPE_ADDED];
    } else if (fieldData[CHANGE_TYPE] === CHANGE_TYPE_REMOVED) {
      fieldContent = fieldData[CHANGE_TYPE_REMOVED];
    }

    let fieldValue = [];

    if (fieldData[TYPE] && fieldData[TYPE] === TYPE_ARRAY) {
      // field is an array
      if (fieldContent == null || !fieldContent.length) {
        // value is not set - null or undefined
        fieldValue.push(EMPTY_VALUE);
      } else {
        // value is an array, get value of each item
        fieldValue = fieldContent.map((item) =>
          getValueFromFieldContent(
            item[KEYWORD_PROP_CONTENT_VALUE],
            intl,
            item[TYPE]
          )
        );
      }
    } else {
      // field is not an array, get its value
      fieldValue.push(
        getValueFromFieldContent(fieldContent, intl, fieldData[TYPE])
      );
    }

    return fieldValue;
  }
  logger.warn(`Invalid Change Item definition: ${fieldData}`);
  return ['-----'];
}

const ChangeItemContentItem = ({item, intl, isLast, showActionLabel}) => {
  const fieldName = utils.TranslateUtils.translate(
    {
      id: `${CHANGE_FIELD_PREFIX}${item[NAME]}`,
      defaultMessage: item[NAME],
    },
    intl
  );
  const fieldValue = getFieldValue(item, intl, isLast);

  const actionLabel = showActionLabel
    ? utils.TranslateUtils.translate(
        I18N[item[CHANGE_TYPE].toUpperCase()],
        intl
      )
    : null;
  const action = fieldName;

  return (
    <div className={styles.dlChangeItemContentItem}>
      <span
        className={classNames(styles.dlChangeActionLabel, styles.dlPadding)}
        title={actionLabel}
      >
        {actionLabel}
      </span>
      <div className={styles.dlChangeAction}>
        <span
          className={classNames(
            styles.dlChangeActionProperty,
            styles.dlPadding
          )}
          title={action}
        >
          {action}
        </span>
        <span className={classNames(styles.dlToLabel, styles.dlPadding)}>
          {item[CHANGE_TYPE] === CHANGE_TYPE_CHANGED
            ? utils.TranslateUtils.translate(I18N.TO, intl)
            : null}
        </span>
        <div className={styles.dlChangeActionValueWrapper}>
          {fieldValue &&
            fieldValue.map((fieldValueItem, idx) => (
              <span
                className={styles.dlChangeActionValue}
                title={fieldValueItem}
                key={idx}
              >
                {fieldValueItem}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
};

ChangeItemContentItem.displayName = 'ChangeItemContentItem';

/* eslint-disable react/no-unused-prop-types */
ChangeItemContentItem.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  isLast: PropTypes.bool,
  intl: intlShape.isRequired,
  showActionLabel: PropTypes.bool,
};

ChangeItemContentItem.defaultProps = {
  isLast: false,
  showActionLabel: false,
};

export default injectIntl(ChangeItemContentItem);
